import { Shell } from "@/components/layout/Shell";
import { useCompra } from "@/hooks/use-data";
import { useModulos } from "@/hooks/use-modulos";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "wouter";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle2, XCircle, Clock, FlaskConical, Loader2 } from "lucide-react";
import { VEICULO_TIPO_LABELS } from "@/lib/constants";
import { METODO_LABELS } from "@/lib/constants";

type Resultado = "sucesso" | "falha" | "expirado";

export default function SimularCheckoutAsaas({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { modulos, isLoading: modulosLoading } = useModulos();
  const { data: compra, isLoading, error } = useCompra(id);
  const [resolvendo, setResolvendo] = useState<Resultado | null>(null);
  const { toast } = useToast();

  if (!modulosLoading && !modulos.creditosPrePagos) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Módulo não disponível</h2>
          <p className="text-muted-foreground max-w-sm">
            O módulo de Créditos Pré-Pagos não está ativo para sua vistoriadora.
          </p>
        </div>
      </Shell>
    );
  }

  const resolver = async (resultado: Resultado) => {
    setResolvendo(resultado);
    try {
      await apiRequest("POST", `/api/creditos/simular-compra/${id}/resolver`, {
        resultado,
      });
      sessionStorage.setItem("ultima_compra_id", id);
      setLocation("/creditos/retorno");
    } catch (err) {
      toast({
        title: "Erro ao Resolver Simulação",
        description: err instanceof Error ? err.message : "Tente novamente",
        variant: "destructive",
      });
      setResolvendo(null);
    }
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto py-12 space-y-4">
        <Badge variant="outline" className="gap-1 mx-auto flex w-fit">
          <FlaskConical className="w-3 h-3" />
          Simulação de Compra — nenhum valor real é cobrado
        </Badge>
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Checkout (simulado)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error ? (
              <div className="text-center space-y-4 py-4">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
                <p className="text-muted-foreground">
                  Não foi possível carregar esta compra simulada.
                </p>
                <Button onClick={() => setLocation("/creditos/comprar")} className="w-full">
                  Voltar
                </Button>
              </div>
            ) : isLoading || !compra ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <>
                <div className="p-4 bg-slate-50 rounded-lg space-y-1 text-sm">
                  <p className="text-muted-foreground">
                    Forma de pagamento: {METODO_LABELS[compra.metodo] || compra.metodo}
                  </p>
                  {(compra.itens || []).map((item) => (
                    <div key={item.tipo_veiculo} className="flex justify-between">
                      <span>
                        {item.quantidade}x {VEICULO_TIPO_LABELS[item.tipo_veiculo]}
                      </span>
                      <span>R$ {Number(item.subtotal).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 flex justify-between font-bold">
                    <span>Total:</span>
                    <span>R$ {Number(compra.valor_total).toFixed(2)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">
                    Ferramenta de QA — escolha o resultado deste pagamento:
                  </p>
                  <Button
                    onClick={() => resolver("sucesso")}
                    disabled={!!resolvendo}
                    className="w-full gap-2"
                  >
                    {resolvendo === "sucesso" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    Aprovar pagamento
                  </Button>
                  <Button
                    onClick={() => resolver("falha")}
                    disabled={!!resolvendo}
                    variant="destructive"
                    className="w-full gap-2"
                  >
                    {resolvendo === "falha" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    Recusar pagamento
                  </Button>
                  <Button
                    onClick={() => resolver("expirado")}
                    disabled={!!resolvendo}
                    variant="outline"
                    className="w-full gap-2"
                  >
                    {resolvendo === "expirado" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    Simular expiração
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
